/**
 * useAttributeData — on-demand loader for attribute-master category definitions.
 *
 * Resolution order (cheapest first):
 *   1. In-memory Zustand store (attributeStore)  — no I/O
 *   2. IndexedDB attr-cache store                — fast local read
 *   3. Remote API (fetchAttributeDefinitions)    — network call
 *
 * Results are written to both IDB and attributeStore so subsequent calls
 * (even after component unmount/remount) are served from cache.
 */

import { useCallback } from 'react'
import { useAttributeStore } from '@/store/attributeStore'
import { fetchAttributeDefinitions } from '@/services/attributeService'
import { getAttrCacheRecord, setAttrCacheRecord } from '@/services/idbService'
import { log } from '@/utils/logger'

export function useAttributeData() {
  const { cache, setCache } = useAttributeStore()

  const loadCategory = useCallback(
    async (category: string): Promise<void> => {
      // 1. Already in memory
      if (cache[category]) return

      // 2. Check IndexedDB
      try {
        const record = await getAttrCacheRecord(category)
        if (record?.attributes?.length) {
          log.info('attributeData IDB hit', { category, count: String(record.attributes.length) })
          setCache(category, record.attributes)
          return
        }
      } catch {
        // IDB read failure is non-fatal — fall through to API
      }

      // 3. Fetch from API
      try {
        const attributes = await fetchAttributeDefinitions(category)
        if (attributes.length) {
          log.info('attributeData API fetch success', { category, count: String(attributes.length) })
          setCache(category, attributes)
          setAttrCacheRecord({ category, attributes, cachedAt: Date.now() }).catch((err) =>
            log.warn('attributeData IDB write failed', { category, error: String(err) })
          )
        } else {
          log.warn('attributeData fetchAttributeDefinitions returned empty', { category })
        }
      } catch (err) {
        log.error(`attributeData failed to load category "${category}"`, err instanceof Error ? err : new Error(String(err)), { category })
      }
    },
    [cache, setCache]
  )

  return { loadCategory }
}
